/**
 * 계정정보찾기 폼 컴포넌트
 * FSD: features/account-recovery/ui
 */

import React, { useState } from "react";
import { RecoveryMode } from "../model/account-recovery.types";
import { useAccountRecoveryForm } from "../hooks/useAccountRecoveryForm";
import { useEmailVerification } from "@/features/signup/hooks/useEmailVerification";
import { showAlert } from "@/shared/lib/alert";
import {
  findIdUseCase,
  resetPasswordUseCase,
} from "../use-cases/account-recovery.use-cases";

interface AccountRecoveryFormProps {
  mode: RecoveryMode;
  onSuccess?: () => void;
}

export function AccountRecoveryForm({
  mode,
  onSuccess,
}: AccountRecoveryFormProps) {
  const {
    formData,
    isLoading,
    emailSent,
    handleChange,
    setIsLoading,
    setEmailSent,
  } = useAccountRecoveryForm(mode);
  const emailVerification = useEmailVerification();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successResult, setSuccessResult] = useState<{
    title: string;
    value: string;
  } | null>(null);

  const isIdMode = mode === "id";

  // 임의 비밀번호 생성 (문자와 숫자 8자리)
  const generateRandomPassword = (): string => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let password = "";
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleSendEmail = async () => {
    if (!formData.email) {
      setErrors({ email: "이메일을 입력해주세요." });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setErrors({ email: "올바른 이메일 형식이 아닙니다." });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // useEmailVerification의 sendVerificationEmail이 이미 백엔드로 메일을 발송함
      const success = await emailVerification.sendVerificationEmail(
        formData.email,
        formData.email.split("@")[0]
      );

      if (success) {
        setEmailSent(true);
      }
    } catch (error: any) {
      const errorMsg = error?.message || "이메일 발송에 실패했습니다.";
      setErrors({ email: errorMsg });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!formData.code) {
      setErrors({ code: "인증번호를 입력해주세요." });
      return;
    }

    const isValid = emailVerification.verifyCode(formData.code);

    if (isValid) {
      handleChange("isEmailVerified", true);
      setEmailSent(false);
      setErrors({});
    } else {
      setErrors({
        code: emailVerification.error || "인증번호가 일치하지 않습니다.",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.isEmailVerified) {
      setErrors({ general: "이메일 인증을 완료해주세요." });
      return;
    }

    if (isIdMode && !formData.email) {
      setErrors({ email: "이메일을 입력해주세요." });
      return;
    }

    if (!isIdMode && (!formData.email || !formData.user_id)) {
      setErrors({
        email: formData.email ? "" : "이메일을 입력해주세요.",
        user_id: formData.user_id ? "" : "아이디를 입력해주세요.",
      });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      if (isIdMode) {
        const result = await findIdUseCase.execute({
          email: formData.email,
        });
        if (result.success) {
          // 성공 결과 표시 (복사 가능)
          setSuccessResult({
            title: "조회된 아이디",
            value: result.user_id || "",
          });
          setErrors({});
        } else {
          // 실패 메시지는 폼에 표시
          setErrors({
            general: result.message || "아이디 조회에 실패했습니다.",
          });
          setSuccessResult(null);
        }
      } else {
        // 임의 비밀번호 생성
        const newPassword = generateRandomPassword();
        const result = await resetPasswordUseCase.execute({
          email: formData.email,
          user_id: formData.user_id!,
          newPassword,
        });
        if (result.success) {
          // 성공 결과 표시 (복사 가능)
          setSuccessResult({
            title: "임시 비밀번호",
            value: newPassword,
          });
          setErrors({});
        } else {
          // 실패 메시지는 폼에 표시
          setErrors({
            general: result.message || "비밀번호 초기화에 실패했습니다.",
          });
          setSuccessResult(null);
        }
      }
    } catch (error: any) {
      const errorMessage = error.message || "요청 처리에 실패했습니다.";
      showAlert(errorMessage);
      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
      {/* 일반 에러 메시지 */}
      {errors.general && (
        <div className="p-3 md:p-4 bg-red-50 border border-red-200 rounded text-xs md:text-sm text-red-600">
          {errors.general}
        </div>
      )}

      {/* 성공 결과 박스 (복사 가능) */}
      {successResult && (
        <div className="p-4 md:p-5 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-xs md:text-sm text-gray-600 mb-2">
            {successResult.title}
          </p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={successResult.value}
              readOnly
              className="flex-1 px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border border-green-300 rounded-lg bg-white font-mono select-all"
              onClick={(e) => (e.target as HTMLInputElement).select()}
            />
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(successResult.value);
                showAlert("복사되었습니다!");
              }}
              className="px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm font-semibold bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors whitespace-nowrap"
            >
              복사
            </button>
            <button
              type="button"
              onClick={() => {
                setSuccessResult(null);
                onSuccess?.();
              }}
              className="px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm font-semibold bg-[#FF7131] text-white rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap"
            >
              확인
            </button>
          </div>
        </div>
      )}

      {/* 이메일 필드 */}
      <div>
        <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
          이메일 <span className="text-red-500">*</span>
        </label>
        <div className="flex flex-col md:flex-row gap-2">
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            disabled={formData.isEmailVerified || emailSent || !!successResult}
            className={`flex-1 px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border rounded-lg focus:outline-none focus:ring-2 disabled:bg-gray-100 ${
              errors.email
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:ring-orange-500"
            }`}
            placeholder="이메일 주소를 입력해주세요"
          />
          <button
            type="button"
            onClick={handleSendEmail}
            disabled={
              formData.isEmailVerified ||
              emailSent ||
              !formData.email ||
              isLoading
            }
            className={`px-4 md:px-6 py-2 md:py-3 text-sm md:text-base font-semibold rounded-lg transition-colors whitespace-nowrap ${
              formData.isEmailVerified || emailSent || isLoading
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-[#FF7131] text-white hover:bg-orange-600"
            }`}
          >
            {isLoading
              ? "발송 중..."
              : formData.isEmailVerified
              ? "인증완료"
              : "메일로 보내기"}
          </button>
        </div>
        {errors.email && (
          <p className="mt-1 text-xs md:text-sm text-red-500">{errors.email}</p>
        )}
      </div>

      {/* 이메일 인증번호 입력 */}
      {emailSent && !formData.isEmailVerified && (
        <div>
          <div className="mb-3 text-xs md:text-sm text-gray-600">
            인증번호 입력 남은 시간:{" "}
            <span className="font-semibold text-orange-600">
              {emailVerification.formattedTime()}
            </span>
          </div>
          <div className="flex flex-col md:flex-row gap-2">
            <div className="flex-1">
              <input
                type="text"
                value={formData.code}
                onChange={(e) => handleChange("code", e.target.value)}
                className={`w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.code
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:ring-orange-500"
                }`}
                placeholder="인증번호를 입력해주세요"
                maxLength={8}
              />
              {errors.code && (
                <p className="mt-1 text-xs md:text-sm text-red-500">
                  {errors.code}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleVerifyEmail}
              disabled={!formData.code}
              className={`px-4 md:px-6 py-2 md:py-3 text-sm md:text-base font-semibold rounded-lg transition-colors whitespace-nowrap ${
                !formData.code
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-[#FF7131] text-white hover:bg-orange-600"
              }`}
            >
              인증
            </button>
          </div>
        </div>
      )}

      {/* 인증 완료 표시 */}
      {formData.isEmailVerified && (
        <p className="text-xs md:text-sm text-green-500 font-semibold">
          ✓ 이메일 인증이 완료되었습니다.
        </p>
      )}

      {/* 비밀번호 찾기 모드 - 아이디 입력 */}
      {!isIdMode && (
        <div>
          <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
            아이디 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.user_id || ""}
            onChange={(e) => handleChange("user_id", e.target.value)}
            disabled={!formData.isEmailVerified}
            className={`w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border rounded-lg focus:outline-none focus:ring-2 disabled:bg-gray-100 ${
              errors.user_id
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:ring-orange-500"
            }`}
            placeholder="아이디를 입력해주세요"
          />
          {errors.user_id && (
            <p className="mt-1 text-xs md:text-sm text-red-500">
              {errors.user_id}
            </p>
          )}
        </div>
      )}

      {/* 제출 버튼 */}
      <button
        type="submit"
        disabled={!formData.isEmailVerified || isLoading || !!successResult}
        className={`w-full py-2 md:py-3 text-sm md:text-base font-semibold rounded-lg transition-colors ${
          !formData.isEmailVerified || isLoading || !!successResult
            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
            : "bg-[#FF7131] text-white hover:bg-orange-600"
        }`}
      >
        {isLoading
          ? "처리 중..."
          : isIdMode
          ? "아이디 찾기"
          : "비밀번호 초기화"}
      </button>
    </form>
  );
}
